/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useEffect } from "react";

import { PlayerControllerType } from "../types";

const createStylesheet = (src: string) => {
  let link = document.querySelector(`link[href="${src}"]`) as HTMLLinkElement;

  if (!link) {
    link = document.createElement("link");
    link.rel = "stylesheet";
    link.as = "style";
    link.href = src;
    document.head.appendChild(link);
  }
};

const createScript = (src: string, type?: string) => {
  // Fetch existing script element by src
  // It may have been added by another intance of this hook
  let script = document.querySelector(
    `script[src="${src}"]`,
  ) as HTMLScriptElement;

  if (!script) {
    // Create script
    script = document.createElement("script") as HTMLScriptElement;
    script.src = src;
    script.async = true;

    if (type) {
      script.type = type;
    }
    // Add script to document body
    document.body.appendChild(script);
  }
};

interface UsePlayerLoaderOptions {
  playerType?: PlayerControllerType;
  sceenicPluginEnabled?: boolean;
}

export const usePlayerLoader = ({
  playerType,
  sceenicPluginEnabled,
}: UsePlayerLoaderOptions = {}) => {
  let playerSrc = "";

  switch (playerType) {
    case PlayerControllerType.JWPlayer:
      playerSrc = "https://cdn.jwplayer.com/libraries/6cWCXvWu.js";
      break;
    case PlayerControllerType.THEOPlayer:
      playerSrc =
        "https://cdn.myth.theoplayer.com/b9fe9045-ed93-4842-8e5a-13b6c34d69ef/THEOplayer.js";
      break;
    case PlayerControllerType.Flowplayer:
      playerSrc =
        "https://cdn.flowplayer.com/releases/native/3/stable/flowplayer.min.js";
      break;
    default:
      break;
  }

  useEffect(() => {
    if (!playerSrc) return;

    let script = document.querySelector(
      `script[src="${playerSrc}"]`,
    ) as HTMLScriptElement;

    if (!script) {
      script = document.createElement("script") as HTMLScriptElement;
      script.src = playerSrc;
      script.async = true;
      document.body.appendChild(script);
    }
  }, [playerSrc]);

  switch (playerType) {
    case PlayerControllerType.THEOPlayer:
      createStylesheet(
        "https://cdn.myth.theoplayer.com/b9fe9045-ed93-4842-8e5a-13b6c34d69ef/ui.css",
      );
      break;
    case PlayerControllerType.Flowplayer:
      createStylesheet(
        "https://cdn.flowplayer.com/releases/native/3/stable/style/flowplayer.css",
      );

      // HLS plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/hls.min.js",
      );
      // AirPlay plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/airplay.min.js",
      );
      // Quality selection plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/qsel.min.js",
      );
      // Audio Track Selection plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/asel.min.js",
      );
      // Float-on-Scroll plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/float-on-scroll.min.js",
      );
      // HTML Subtitles plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/subtitles.min.js",
      );
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/playlist.min.js",
      );
      // Keyboard plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/keyboard.min.js",
      );
      // Wowza Video Platform Integration plugin
      createScript(
        "https://cdn.flowplayer.com/releases/native/3/stable/plugins/ovp.min.js",
      );
      break;
    default:
      break;
  }

  if (sceenicPluginEnabled) {
    // Sceenic plugin
    createScript(
      "https://iframe-service-backend.management.sceenic.co/sdk",
      "module",
    );
  }
};
